import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout from '@/components/LegalPageLayout';

const SECTIONS = [
  {
    id: 'acceptance',
    title: 'Acceptance of terms',
    content: (
      <>
        <p>
          These Terms of Service (the <strong>"Terms"</strong>) govern your access to and use of the Zap AI website at <a href="https://www.zapai.com.au" className="text-blue-600 hover:underline">www.zapai.com.au</a> and any related services we provide directly through this site (the <strong>"Site"</strong>).
        </p>
        <p>
          By accessing or using the Site, you agree to be bound by these Terms. If you do not agree, do not use the Site.
        </p>
        <p>
          Use of our products (for example, Zalma) is governed by separate terms of service that apply to that product. Where a product terms is more specific, the product terms apply.
        </p>
      </>
    ),
  },
  {
    id: 'about-zap-ai',
    title: 'About Zap AI',
    content: (
      <>
        <p>
          Zap AI is an Australian software company building AI-powered platforms for service businesses. The Site is our company website, used to share information about who we are, the products we build, and how to get in touch.
        </p>
      </>
    ),
  },
  {
    id: 'use-of-the-site',
    title: 'Use of the Site',
    content: (
      <>
        <p>You agree to use the Site only for lawful purposes and in a way that does not infringe the rights of others. You agree not to:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Use the Site for any unlawful purpose or in violation of any applicable law or regulation.</li>
          <li>Attempt to gain unauthorised access to the Site or any related system.</li>
          <li>Interfere with or disrupt the integrity or performance of the Site, including by introducing viruses or other harmful code.</li>
          <li>Use any automated means (such as scrapers or bots) to extract content from the Site without our written permission.</li>
          <li>Misrepresent your identity or affiliation when contacting us.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'demo-requests',
    title: 'Demo and sales enquiries',
    content: (
      <>
        <p>
          When you submit a demo request or sales enquiry, you confirm the information you provide is accurate. We will use that information to respond to your enquiry, in accordance with our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>.
        </p>
        <p>
          A demo request does not create a binding contract. If you decide to use one of our products, your relationship will be governed by that product's terms.
        </p>
      </>
    ),
  },
  {
    id: 'intellectual-property',
    title: 'Intellectual property',
    content: (
      <>
        <p>
          The Site, including its content, design, branding, and code, is the property of Zap AI and its licensors. We grant you a limited, non-exclusive, non-transferable, revocable licence to access the Site for personal, non-commercial use.
        </p>
        <p>
          You may not copy, modify, distribute, or commercially exploit any part of the Site without our written permission. The "Zap AI" name and logo are trademarks of Zap AI.
        </p>
      </>
    ),
  },
  {
    id: 'privacy',
    title: 'Privacy',
    content: (
      <>
        <p>
          Our handling of personal information is described in the <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>, which forms part of these Terms.
        </p>
      </>
    ),
  },
  {
    id: 'third-party',
    title: 'Third-party content',
    content: (
      <>
        <p>
          The Site may include links to third-party websites or services. We are not responsible for the content, terms, or privacy practices of those third parties. Inclusion of a link does not imply endorsement.
        </p>
      </>
    ),
  },
  {
    id: 'disclaimers',
    title: 'Disclaimers',
    content: (
      <>
        <p>
          To the maximum extent permitted by law, the Site is provided <strong>"as is"</strong> and <strong>"as available"</strong>. We make no warranties, express or implied, about the Site, including warranties of accuracy, fitness for a particular purpose, or uninterrupted operation.
        </p>
        <p>
          Some warranties under Australian Consumer Law cannot be excluded. Nothing in these Terms is intended to exclude, restrict, or modify any consumer guarantees that apply to you.
        </p>
      </>
    ),
  },
  {
    id: 'liability',
    title: 'Limitation of liability',
    content: (
      <>
        <p>
          To the maximum extent permitted by law, Zap AI's total liability arising out of or in connection with your use of the Site is limited to AUD $100.
        </p>
        <p>
          We are not liable for indirect, incidental, special, consequential, or punitive damages, including lost profits, lost data, or business interruption, even if we have been advised of the possibility.
        </p>
        <p>
          These limitations do not apply to liability that cannot be excluded under Australian Consumer Law, fraud, or wilful misconduct.
        </p>
      </>
    ),
  },
  {
    id: 'governing-law',
    title: 'Governing law and disputes',
    content: (
      <>
        <p>
          These Terms are governed by the laws of New South Wales, Australia, without regard to conflict of laws rules. The parties submit to the non-exclusive jurisdiction of the courts of New South Wales for any dispute arising out of or in connection with these Terms.
        </p>
        <p>
          Before commencing legal proceedings, the parties agree to attempt to resolve any dispute in good faith through direct discussion.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to these terms',
    content: (
      <>
        <p>
          We may update these Terms from time to time. We will post the updated version on this page and update the "Last updated" date at the top. Your continued use of the Site after the updated Terms take effect indicates your acceptance.
        </p>
      </>
    ),
  },
  {
    id: 'general',
    title: 'General',
    content: (
      <>
        <p>
          <strong>Entire agreement.</strong> These Terms, together with the Privacy Policy, constitute the entire agreement between you and Zap AI relating to the Site.
        </p>
        <p>
          <strong>Severability.</strong> If any provision is held unenforceable, the remaining provisions remain in full force and effect.
        </p>
        <p>
          <strong>No waiver.</strong> Our failure to enforce a provision is not a waiver of that provision or any other.
        </p>
      </>
    ),
  },
  {
    id: 'contact',
    title: 'Contact',
    content: (
      <>
        <p>
          If you have questions about these Terms, contact us at <a href="mailto:info@zapai.com.au" className="text-blue-600 hover:underline">info@zapai.com.au</a>.
        </p>
      </>
    ),
  },
];

export default function TermsPage() {
  return (
    <LegalPageLayout
      title="Terms of Service"
      lastUpdated="May 2, 2026"
      sections={SECTIONS}
    />
  );
}
